import React, { useState, useEffect } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { ModeContextProvider } from '../../common/contexts/mode-context';
import type { User } from '../../types/User';
import DisabledFormPage from './disabled-form-page';
import CompletedFormPage from './completed-form-page';
import LoadingFormPage from './loading-form-page';
import { FormSubmitter } from '../form-submitter/form-submitter';
import FileRequestAPI from '../../api/FileRequest';
import Footer from './footer';
import ThemeStyle from '../../common/components/theme-style';
import ThemeHeader from '../../common/components/theme-header';
import { useShouldRenderBlueprint } from '../../common/useShouldRenderBlueprint';
import { MODE, FILE_REQUEST_VISIBILITY } from '../../constants';
import resin from '../../common/utils/resin/resin';
import PreviewHeader from './preview-header';
import { ADMIN_COLOR_LOGO } from '../builder/builder-context/builder-constant';

import css from './submission-page.module.scss';
import NewThemeHeader from '../new-builder-content/new-theme-header';
import isAnonymousUser from '../../common/utils/isAnonymousUser';
import { useFetchSubmissionPageData } from './hooks/fetchSubmissionPageData';
import { SpoofedUrlRedirectPage } from './spoofed-url-redirect-page/spoofed-url-redirect-page';

type Props = {
  isPreview?: boolean;
  urlId?: string;
  fileRequestId?: string;
  currentUser: User;
};

const recordResinAction = (urlId: string) => {
  const inFrame = window.location !== window.parent.location;
  const component = inFrame ? 'submissionpage|iframe' : 'submissionpage';

  resin.recordAction({
    action: 'programmatic',
    page: 'file-request-submission',
    feature: 'filerequest',
    component,
    target: 'pageloaded',
    data: {
      file_request_url_id: urlId,
    },
  });
};

const SubmissionPage = (props: Props) => {
  const { urlId = '', fileRequestId = '', currentUser, isPreview } = props;
  const [filesAmount, setFilesAmount] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const isFormPreview = !isEmpty(fileRequestId) && isPreview;

  const { fetch, fileRequest, type, isDisabled, isLoading, redirectUrl } =
    useFetchSubmissionPageData();
  // Should be addressed in FORMS-21228
  const shouldRenderBlueprint = useShouldRenderBlueprint(type);

  const handleSuccessfulSubmission = (amount: number) => {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2345
    setFilesAmount(amount);
    setIsSubmitted(true);
  };

  useEffect(() => {
    const loadBaseStyles = () =>
      import('box-ui-elements/es/elements/common/base.scss');
    if (!isPreview) {
      // No need to load base style in preview page since it's already loaded in the EUA https://git.dev.box.net/Box/EndUserApp/blob/master/src/main.js#L9-L10
      // Load it twice may lead to possible UI issues eg: FORMS-13378
      loadBaseStyles();
    }
    if (isFormPreview) {
      // This path is for Forms preview only
      fetch(fileRequestId, FileRequestAPI.getFileRequest);
    } else if (!isEmpty(urlId)) {
      const getByUrlId =
        isAnonymousUser(currentUser) || !currentUser?.id
          ? FileRequestAPI.getPublicFileRequestByUrlId
          : FileRequestAPI.getFileRequestByUrlId;
      fetch(urlId, getByUrlId);
      recordResinAction(urlId);
    }
  }, [currentUser, urlId, fileRequestId, isFormPreview, fetch, isPreview]);

  const shouldRenderDisabledFormPage =
    (isEmpty(urlId) && isEmpty(fileRequestId)) || (isDisabled && !isPreview);

  if (shouldRenderDisabledFormPage) {
    return (
      <ModeContextProvider mode={type}>
        <DisabledFormPage />
      </ModeContextProvider>
    );
  }

  if (redirectUrl) return <SpoofedUrlRedirectPage verifiedUrl={redirectUrl} />;
  if (isLoading) return <LoadingFormPage />;

  const isAuthenticationRequired =
    FILE_REQUEST_VISIBILITY.AUTHENTICATED === get(fileRequest, 'visibility');

  if (isSubmitted) {
    return (
      <ModeContextProvider mode={type}>
        {isPreview && <PreviewHeader type={type} isDisabled={isDisabled} />}
        <CompletedFormPage
          isAuthenticationRequired={isAuthenticationRequired}
          numFiles={filesAmount}
        />
      </ModeContextProvider>
    );
  }

  const form = get(fileRequest, 'form');
  const folderId = get(fileRequest, 'folder.id');
  const isSameEnterprise = get(fileRequest, 'isSameEnterprise', false);

  const logo = get(form, 'content.theme.logo.value', '');
  const color = get(form, 'content.theme.colors.primary.value', '');
  const logoKey = get(form, 'content.theme.logo.key');
  const logoBackgroundColor =
    logo && logoKey === ADMIN_COLOR_LOGO && color ? `#${color}` : '#ffffff';

  const pageLoadTestId =
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2367
    type === MODE.FORM
      ? 'forms-submission-page'
      : 'file-request-submission-page';

  const contentClassName = classNames({
    [css.newSubmissionPage]: shouldRenderBlueprint,
    [css.submissionPage]: !shouldRenderBlueprint,
    [css.submissionPageWithHeader]: isPreview && isDisabled,
    [css.withTheme]: !!color || !!logo,
    [css.withLogo]: !!logo,
  });

  const enterpriseName = isFormPreview
    ? currentUser?.enterprise?.name
    : // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
      fileRequest?.enterpriseName;

  return (
    <ModeContextProvider mode={type}>
      <div className={css.submissionPageOverlay} data-testid={pageLoadTestId}>
        <ThemeStyle color={color} wrapperClass={css.submissionPageOverlay} />
        {!shouldRenderBlueprint && (
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          <ThemeHeader color={color} logo={logo} page="builder" />
        )}
        {isPreview && <PreviewHeader type={type} isDisabled={isDisabled} />}
        <div className={contentClassName}>
          <div className={css.formSubmitter}>
            {shouldRenderBlueprint && (
              <NewThemeHeader
                alt="test-header"
                color={color}
                logo={logo}
                isBuilder={false}
                logoBackgroundColor={logoBackgroundColor}
              />
            )}
            {/* Only if mode is forms should css.formContent */}
            <div className={css.formContent}>
              <FormSubmitter
                color={color}
                // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
                fileRequestId={get(fileRequest, 'id')}
                // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
                folderId={folderId}
                // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
                form={form}
                isAuthenticationRequired={isAuthenticationRequired}
                isPreview={!!isPreview}
                isSameEnterprise={!!isSameEnterprise}
                logo={logo}
                onSuccessfulSubmission={handleSuccessfulSubmission}
                urlId={urlId}
              />
            </div>
          </div>
          <Footer enterpriseName={enterpriseName} formType={type} />
        </div>
      </div>
    </ModeContextProvider>
  );
};

export { SubmissionPage };
export default SubmissionPage;
